import React, { Suspense, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./common/Header";
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-image-crop/dist/ReactCrop.css'
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from "./common/Sidebar";
import useLocalStorage from "./utils/RedirectRoute";
import { ToastContainer } from "react-toastify";
import SuccessModal from "./common/SuccessModal";
import CommonLoader from "./common/CommonLoader";
import EmployeeActivity from "./split/Employee/EmployeeActivity.js";
import LicenseUpdate from "./common/license/LicenseUpdate.js";
import { GlobalProvider } from "./context/GlobalContext";

// Lazy imports
const Login = React.lazy(() => import("./common/Login"));
const Content = React.lazy(() => import("./split/Dashboard/Content"));
const ScreenShots = React.lazy(() => import("./split/ScreenShot/Screenshots"));
const Application_act = React.lazy(() => import("./split/Application_activity"));
const Organization = React.lazy(() => import("./split/Organization"));
const URL_Track = React.lazy(() => import("./split/Url-Track"));
const Rating = React.lazy(() => import("./split/KT/Rating/Rating.js"));
const Organization_details = React.lazy(() => import("./split/Organization_details"));
const Attendence = React.lazy(() => import("./split/Attendence/Attendence"));
const TimeOff = React.lazy(() => import("./split/Attendence/TimeOff"));
const Department = React.lazy(() => import("./split/Department/Department"));
const Designation = React.lazy(() => import("./split/Designation/Designation"));
const Employee = React.lazy(() => import("./split/Employee/Employee"));
const Locations = React.lazy(() => import("./split/Locations/Locations"));
const Timesheet = React.lazy(() => import("./split/Timesheet/Timesheet"));

const Keystoke = React.lazy(() => import("./split/Keystroke/Keystoke"));
const to_do_list = React.lazy(() => import("./split/ToDo-List/ToDoList"));
const sub_depart = React.lazy(() => import("./split/Department/SubDepartment"));
const Project_Management = React.lazy(() => import("./split/Project_Management/Project_Management"));
const Add_project = React.lazy(() => import("./split/Project_Management/Add_project"));
const Client = React.lazy(() => import("./split/Project_Management/Client"));
const Screen_interval = React.lazy(() => import("./split/screen_interval/Screen_interval"));
const TODO = React.lazy(() => import("./split/TODO"));
const RoleAccess = React.lazy(() => import("./split/Department/RoleAccess"));
const EmployeeDevice = React.lazy(() => import('./split/Employee/EmployeeDevice'))
const ConfiguredDevice = React.lazy(() => import('./split/Employee/ConfiguredDevice'))
const ProjectDetails = React.lazy(() => import("./split/ProjectDetails/ProjectDetails"));
const Calendar = React.lazy(() => import("./split/Calendar/Calendar.js"));
const Subscription = React.lazy(() => import("./split/Subscription/Subscription"))
const Schedule = React.lazy(() => import("./split/Employee/Scheduling.js"))
const PendingDevice = React.lazy(() => import("./split/PendingDevice/PendingDevice.js"))
const Category = React.lazy(() => import("./split/KT/Category/Category.js"))
const Transfer = React.lazy(() => import('./split/KT/KTtransfer/Transfer.js'))
const Document = React.lazy(() => import('./split/KT/KTDocument/Document.js'))
const KTDetail = React.lazy(() => import('./split/KT/KTtransfer/KTDetailsView.js'))
const EmployeeLog = React.lazy(() => import("./split/Employee/EmployeeLog.js"))
const Kafaa = React.lazy(() => import("./split/KafaaUpload/Index.js"))
const TaskTracking = React.lazy(() => import("./split/Project_Management/TaskTracking.js"))
const Track_Url = React.lazy(() => import("./split/Project_Management/Track_Url.js"))
const Track_View = React.lazy(() => import("./split/Project_Management/Track_View.js"))
const WhiteList = React.lazy(() => import("./split/WhiteList/View.js"))
const Tracking = React.lazy(() => import("./split/Tracking"));
const OverallReport = React.lazy(() => import("./split/Report/OverallReport"));
const TaskReport = React.lazy(() => import("./split/Report/TaskReport"));
const ProjectOverallReport = React.lazy(() => import("./split/Report/ProjectOverallReport"));
const ScreenshotAssignment = React.lazy(() => import("./split/Employee/ScreenshotAssignment"));

const App = () => {
  const [token, setToken] = useLocalStorage("token");
  const lang = localStorage.getItem("lang");

  useEffect(() => {
    if (!lang) {
      localStorage.setItem('lang', 'ar');
    }
  }, []);

  let routeList = [];
  try {
    const parsedToken = token;
    const rawMenu = parsedToken?.role_access_data?.menu || [];
    if (Array.isArray(rawMenu)) {
      routeList = rawMenu;
    } else if (typeof rawMenu === 'object' && rawMenu !== null) {
      routeList = Object.keys(rawMenu).filter(key => rawMenu[key]?.read === true);
    }
  } catch (error) {
    console.error("Failed to parse token:", error);
  }

  // Always accessible routes
  const publicRoutes = ["/login", "/admin-login", "/", "/license_upload"];

  routeList = [
    ...routeList,
    ...publicRoutes,
    "/*",
    "/dashboard",
    "/project-details",
    "/category",
    "/kt-view",
    "/transfer",
    "/document",
    "/kt-upload",
    "/rating",
    "/pending-device",
    "/configured-device",
    "/kt_Details",
    "/employee-log",
    "/Kafaa",
    "/case_view",
    "/tracking",

    "/overall-employee-report",
    "/task-report",
    "/project-report",
    "/screenshot-assignment"
  ];

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [search, setSearch] = useState();

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleFilter = (value) => {
    setFilterValue(value);
  };

  // Dashboard wrapper for authenticated pages
  const DashboardWrapper = ({ Component, isPublic }) => {
    const location = useLocation();

    if (!token && !isPublic) {
      const userType = localStorage.getItem('userType');
      const redirectUrl = userType === '1' ? "/admin-login" : "/login";
      return <Navigate to={redirectUrl} state={{ from: location }} replace />;
    }
    return (
      <div className="page">
        {!isPublic && token && <Header toggleSidebar={toggleSidebar} search={search} setSearch={setSearch} />}
        {!isPublic && token && <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />}
        <div className={!isPublic ? "content !pt-[4.5rem]" : ""}>
          <div className="main-content">
            <Component search={search} setSearch={setSearch} filters={filterValue} setFilters={handleFilter} />
          </div>
        </div>
      </div>
    );
  };

  const Dashboard = (Component, isPublic = false) => <DashboardWrapper Component={Component} isPublic={isPublic} />;

  // Login wrapper for redirecting logged-in users away from login page
  const LoginSecureWrapper = ({ Component, isAdminRoute }) => {
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";

    if (token?.id) {
      return <Navigate to={from} replace />;
    }
    return <Component isAdminRoute={isAdminRoute} />;
  };

  const LoginSecure = (Component, isAdminRoute = false) => <LoginSecureWrapper Component={Component} isAdminRoute={isAdminRoute} />;

  const routes = [
    { path: "/login", element: LoginSecure(Login, false), public: true },
    { path: "/admin-login", element: LoginSecure(Login, true), public: true },
    { path: "/", element: LoginSecure(Login, false), public: true },
    { path: "/dashboard", element: Dashboard(Content) },
    { path: "/category", element: Dashboard(Category) },
    { path: "/rating", element: Dashboard(Rating) },
    { path: "/screen-shots", element: Dashboard(ScreenShots) },
    { path: "/application-track", element: Dashboard(Application_act) },
    { path: "/todo", element: Dashboard(TODO) },
    { path: "/organization", element: Dashboard(Organization) },
    { path: "/Url-track", element: Dashboard(URL_Track) },
    { path: "/organization_details", element: Dashboard(Organization_details) },
    { path: "/attendence", element: Dashboard(Attendence) },
    { path: "/time-off", element: Dashboard(TimeOff) },
    { path: "/department", element: Dashboard(Department) },
    { path: "/designation", element: Dashboard(Designation) },
    { path: "/employee", element: Dashboard(Employee) },
    { path: "/locations", element: Dashboard(Locations) },
    { path: "/timesheet", element: Dashboard(Timesheet) },

    { path: "/keystroke", element: Dashboard(Keystoke) },
    { path: "/todo-list", element: Dashboard(to_do_list) },
    { path: "/sub_depart", element: Dashboard(sub_depart) },
    { path: "/role-access", element: Dashboard(RoleAccess) },
    { path: "/project", element: Dashboard(Project_Management) },
    { path: "/client", element: Dashboard(Client) },
    { path: "/screen-interval", element: Dashboard(Screen_interval) },
    { path: "/add-project", element: Dashboard(Add_project) },
    { path: "/employee-device", element: Dashboard(EmployeeDevice) },
    { path: "/project-details", element: Dashboard(ProjectDetails) },
    { path: "/subscription", element: Dashboard(Subscription, true), public: true },
    { path: "/calendar", element: Dashboard(Calendar) },
    { path: "/schedule", element: Dashboard(Schedule) },
    { path: "/pending-device", element: Dashboard(PendingDevice) },
    { path: "/configured-device", element: Dashboard(ConfiguredDevice) },
    { path: "/kt-view", element: Dashboard(Transfer) },
    { path: "/document", element: Dashboard(Document) },
    { path: "/kt_Details", element: Dashboard(KTDetail) },
    { path: "/employee-log", element: Dashboard(EmployeeLog) },
    { path: "/Kafaa", element: Dashboard(Kafaa) },
    { path: "/task_tracking", element: Dashboard(TaskTracking) },
    { path: "/case_tracking", element: Dashboard(Track_Url) },
    { path: "/case_view", element: Dashboard(Track_View) },
    { path: "/tracking", element: Dashboard(Tracking) },
    // { path: "/white_list", element: Dashboard(WhiteList) },
    { path: "/employee-activity", element: Dashboard(EmployeeActivity) },
    { path: "/overall-employee-report", element: Dashboard(OverallReport) },
    { path: "/task-report", element: Dashboard(TaskReport) },
    { path: "/project-report", element: Dashboard(ProjectOverallReport) },
    { path: "/screenshot-assignment", element: Dashboard(ScreenshotAssignment) },
    { path: "/license_upload", element: Dashboard(LicenseUpdate, true), public: true },
    { path: "/*", element: <Navigate to="/dashboard" /> }
  ];

  // Keep public routes always accessible
  const filteredRoutes = routes.filter(route =>
    publicRoutes.includes(route.path) || routeList.includes(route.path)
  );

  const LoaderDash = () => {
    const location = useLocation();
    return <CommonLoader />;
  };

  return (
    <GlobalProvider>
      <Router>
        <Suspense fallback={<LoaderDash />}>
          <ToastContainer />
          <SuccessModal />
          <Routes>
            {filteredRoutes.map((route, index) => (
              <Route key={index} path={route.path} element={route.element} />
            ))}
          </Routes>
        </Suspense>
      </Router>
    </GlobalProvider>
  );
};

export default App;
